import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Lightbox = ({ creatives, index, onClose, onPrev, onNext }) => {
  const creative = creatives[index];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  // Preload neighbouring images for snappier nav
  useEffect(() => {
    const preload = (i) => {
      const c = creatives[i];
      if (!c) return;
      const img = new Image();
      img.src = c.image_url;
    };
    preload((index + 1) % creatives.length);
    preload((index - 1 + creatives.length) % creatives.length);
  }, [index, creatives]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(8, 8, 8, 0.96)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={creative?.description || 'Creative preview'}
      data-testid="creatives-lightbox"
    >
      {/* Close */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
        data-cursor-hover
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      {/* Prev */}
      {creatives.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-6 md:left-10 w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10"
          style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
          data-cursor-hover
          data-testid="lightbox-prev"
          aria-label="Previous"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Next */}
      {creatives.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-6 md:right-10 w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10"
          style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
          data-cursor-hover
          data-testid="lightbox-next"
          aria-label="Next"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
          className="max-w-[90vw] max-h-[80vh] flex flex-col items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={creative.image_url}
            alt={creative.description}
            className="max-w-full max-h-[75vh] object-contain"
            style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}
          />
          {/* Caption */}
          <div className="mt-6 text-center">
            <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: '#C5A55A', fontFamily: 'Nohemi, sans-serif' }}>
              {creative.type}
            </p>
            <p className="text-sm md:text-base" style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Nohemi, sans-serif' }}>
              {creative.description}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Counter */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.3em]"
        style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Nohemi, sans-serif' }}
        data-testid="lightbox-counter"
      >
        {String(index + 1).padStart(2, '0')} <span style={{ color: '#C5A55A', margin: '0 8px' }}>/</span> {String(creatives.length).padStart(2, '0')}
      </div>
    </motion.div>
  );
};

const CreativesGallery = ({ creatives }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const open = (i) => setActiveIndex(i);
  const close = useCallback(() => setActiveIndex(null), []);
  const prev = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i - 1 + creatives.length) % creatives.length)),
    [creatives.length]
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i + 1) % creatives.length)),
    [creatives.length]
  );

  if (!creatives || creatives.length === 0) return null;

  return (
    <>
      {/* Masonry grid via CSS columns — preserves natural aspect ratios */}
      <div
        className="columns-1 sm:columns-2 lg:columns-3 gap-5"
        data-testid="creatives-grid"
      >
        {creatives.map((creative, index) => (
          <button
            key={index}
            onClick={() => open(index)}
            className="group relative block w-full mb-5 overflow-hidden text-left break-inside-avoid"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
            data-cursor-hover
            data-testid={`creative-item-${index}`}
            aria-label={`Open ${creative.description}`}
          >
            <div className="overflow-hidden">
              <img
                src={creative.image_url}
                alt={creative.description}
                loading="lazy"
                className="w-full h-auto block transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                style={{ willChange: 'transform' }}
              />
            </div>

            {/* Hover overlay with caption */}
            <div
              className="absolute inset-x-0 bottom-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: 'linear-gradient(to top, rgba(8,8,8,0.92) 0%, rgba(8,8,8,0.55) 60%, transparent 100%)',
              }}
            >
              <p
                className="text-[10px] uppercase tracking-[0.25em] mb-1"
                style={{ color: '#C5A55A', fontFamily: 'Nohemi, sans-serif' }}
              >
                {creative.type}
              </p>
              <p
                className="text-sm leading-snug"
                style={{ color: 'rgba(255,255,255,0.95)', fontFamily: 'Nohemi, sans-serif' }}
              >
                {creative.description}
              </p>
            </div>

            {/* Number index — top-right, golden, subtle */}
            <div
              className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                color: '#C5A55A',
                backgroundColor: 'rgba(8,8,8,0.55)',
                fontFamily: 'Nohemi, sans-serif',
                backdropFilter: 'blur(8px)',
              }}
            >
              {String(index + 1).padStart(2, '0')}
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {activeIndex !== null && (
          <Lightbox
            creatives={creatives}
            index={activeIndex}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CreativesGallery;
